import { curvanceConfigFor } from '@1delta/data-sdk'
import {
  CurvanceCentralRegistryAbi,
  CurvanceCTokenAbi,
  CurvanceMarketManagerAbi,
} from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { Call } from '../../../utils/multicall'

/**
 * User-data call build for Curvance. Positions are keyed by OWNER ADDRESS —
 * one position per user per market, no ids and no sub-accounts (the
 * River/Inverse/Resupply shape).
 *
 * DISCOVERY-FIRST, but for the opposite reason to Resupply: nothing here is
 * expensive, the problem is that Curvance has 25 markets × 2 legs and a typical
 * user is in one of them. `assetsOf(account)` on each MarketManager returns the
 * cTokens the account actually holds a position in, so the shared batch only
 * gets slots for markets the user is really in — 6 calls instead of 150.
 *
 * per ACTIVE market (discovery order):
 *   accountAssets(account)            — cooldownTimestamp (the hold period)
 *   statusOf(account)                 — (collateral, maxDebt, debt) in WAD USD
 *   balanceOf / collateralPosted / debtBalance per leg
 *
 * The roster (market → its two cTokens) is cached per chain+lender; the
 * per-user active set is cached briefly and re-used by the parser so a state
 * change between build and parse cannot desync the slicer from the call list.
 */

const ROSTER_TTL_MS = 5 * 60_000
const DISCOVERY_TTL_MS = 60_000

/** Market-level reads, then 4 per leg. Keep in lock-step with the parser. */
export const CURVANCE_MARKET_READS = 2
export const CURVANCE_READS_PER_TOKEN = 4

/** A cToken and the underlying it wraps — resolved once, cached with the roster. */
export interface CurvanceTokenRef {
  cToken: string
  /** Lowercase `asset()` — the address our public rows are keyed on. */
  asset: string
}

interface MarketRosterEntry {
  marketManager: string
  cTokens: CurvanceTokenRef[]
}

interface MarketRoster {
  markets: MarketRosterEntry[]
  at: number
}

export interface CurvanceActivePosition extends MarketRosterEntry {
  /** The subset of `cTokens` the account actually has a position in. */
  activeTokens: CurvanceTokenRef[]
}

export interface CurvanceDiscovery {
  positions: CurvanceActivePosition[]
  at: number
}

const rosterCache = new Map<string, MarketRoster>()
const discoveryCache = new Map<string, CurvanceDiscovery>()
const rosterKey = (chainId: string, lender: string) => `${chainId}:${lender}`
const discoveryKey = (chainId: string, lender: string, account: string) =>
  `${chainId}:${lender}:${account.toLowerCase()}`

/** The parse phase needs the exact active set the build phase used. */
export const getCachedCurvanceDiscovery = (
  chainId: string,
  lender: string,
  account: string,
): CurvanceDiscovery | undefined =>
  discoveryCache.get(discoveryKey(chainId, lender, account))

/** Discover markets + their listed cTokens, cached per chain+lender. */
const fetchRoster = async (
  chainId: string,
  lender: string,
  centralRegistry: string,
  allowlist?: string[] | null,
): Promise<MarketRoster> => {
  const cached = rosterCache.get(rosterKey(chainId, lender))
  if (cached && Date.now() - cached.at < ROSTER_TTL_MS) return cached

  let managers: string[] = []
  const [raw] = (await multicallRetryUniversal({
    chain: chainId,
    calls: [{ address: centralRegistry, name: 'marketManagers', params: [] }],
    abi: CurvanceCentralRegistryAbi as any,
    allowFailure: true,
  })) as any[]
  if (Array.isArray(raw)) {
    managers = raw.filter(
      (a: any) => typeof a === 'string' && a.startsWith('0x'),
    )
  }
  if (allowlist && allowlist.length > 0) {
    const allow = new Set(allowlist.map((m) => m.toLowerCase()))
    managers = managers.filter((m) => allow.has(m.toLowerCase()))
  }
  if (managers.length === 0) {
    const empty = { markets: [], at: Date.now() }
    rosterCache.set(rosterKey(chainId, lender), empty)
    return empty
  }

  const listed = (await multicallRetryUniversal({
    chain: chainId,
    calls: managers.map((mm) => ({
      address: mm,
      name: 'queryTokensListed',
      params: [],
    })),
    abi: managers.map(() => CurvanceMarketManagerAbi) as any,
    allowFailure: true,
  })) as any[]

  const withTokens = managers
    .map((marketManager, i) => ({
      marketManager,
      addresses: Array.isArray(listed[i])
        ? (listed[i].filter(
            (t: any) => typeof t === 'string' && t.startsWith('0x'),
          ) as string[])
        : [],
    }))
    .filter((m) => m.addresses.length > 0)

  // Resolve every cToken's underlying ONCE and cache it with the roster. The
  // public rows are keyed on the underlying, and pairing a leg back to its row
  // by position would silently swap the two on markets where BOTH legs are
  // borrowable (eBTC/WBTC is one today).
  const flat = withTokens.flatMap((m) =>
    m.addresses.map((cToken) => ({ mm: m.marketManager, cToken })),
  )
  let assets: any[] = []
  try {
    assets = (await multicallRetryUniversal({
      chain: chainId,
      calls: flat.map((f) => ({
        address: f.cToken,
        name: 'asset',
        params: [],
      })),
      abi: flat.map(() => CurvanceCTokenAbi) as any,
      allowFailure: true,
    })) as any[]
  } catch {
    assets = []
  }
  const assetByToken = new Map<string, string>()
  flat.forEach((f, i) => {
    const a = assets[i]
    if (typeof a === 'string' && a.startsWith('0x')) {
      assetByToken.set(f.cToken.toLowerCase(), a.toLowerCase())
    }
  })

  const markets = withTokens
    .map((m) => ({
      marketManager: m.marketManager,
      cTokens: m.addresses
        .map((cToken) => ({
          cToken,
          asset: assetByToken.get(cToken.toLowerCase()) ?? '',
        }))
        // A leg whose `asset()` we could not read cannot be attached to a row,
        // so drop it rather than emit a position with no market.
        .filter((t) => t.asset !== ''),
    }))
    .filter((m) => m.cTokens.length > 0)

  const roster = { markets, at: Date.now() }
  rosterCache.set(rosterKey(chainId, lender), roster)
  return roster
}

/**
 * `assetsOf` per market, chunked, keeping only markets where the account holds
 * something. A chunk that fails outright is SKIPPED rather than reported empty
 * — "no position" and "we could not read" are different answers, and the caller
 * acts on them differently.
 */
const discoverPositions = async (
  chainId: string,
  markets: MarketRosterEntry[],
  account: string,
): Promise<CurvanceActivePosition[]> => {
  const CHUNK = 15
  const active: CurvanceActivePosition[] = []
  for (let i = 0; i < markets.length; i += CHUNK) {
    const slice = markets.slice(i, i + CHUNK)
    let res: any[] = []
    try {
      res = (await multicallRetryUniversal({
        chain: chainId,
        calls: slice.map((m) => ({
          address: m.marketManager,
          name: 'assetsOf',
          params: [account],
        })),
        abi: slice.map(() => CurvanceMarketManagerAbi) as any,
        allowFailure: true,
      })) as any[]
    } catch {
      continue
    }
    slice.forEach((m, j) => {
      const assets = res[j]
      if (!Array.isArray(assets) || assets.length === 0) return
      // `assetsOf` reports every cToken the account has an OPEN position in
      // (collateral or debt). Intersect with the market's listed pair so a
      // stale entry cannot widen the slice.
      const open = new Set(
        assets
          .filter((a: any) => typeof a === 'string')
          .map((a: string) => a.toLowerCase()),
      )
      const activeTokens = m.cTokens.filter((t) =>
        open.has(t.cToken.toLowerCase()),
      )
      if (activeTokens.length === 0) return
      active.push({ ...m, activeTokens })
    })
  }
  return active
}

export const buildCurvanceUserCall = async (
  chainId: string,
  lender: string,
  account: string,
): Promise<Call[]> => {
  const cfg = curvanceConfigFor(lender, chainId)
  const key = discoveryKey(chainId, lender, account)
  const stash = (positions: CurvanceActivePosition[]) =>
    discoveryCache.set(key, { positions, at: Date.now() })

  if (!cfg?.centralRegistry) {
    stash([])
    return []
  }

  let roster: MarketRoster
  try {
    roster = await fetchRoster(
      chainId,
      lender,
      cfg.centralRegistry,
      cfg.marketAllowlist,
    )
  } catch {
    // Discovery failure ⇒ report nothing rather than an empty book. A stale
    // roster inside the TTL is still served by `fetchRoster`.
    return []
  }
  if (roster.markets.length === 0) {
    stash([])
    return []
  }

  const cached = discoveryCache.get(key)
  const positions =
    cached && Date.now() - cached.at < DISCOVERY_TTL_MS
      ? cached.positions
      : await discoverPositions(chainId, roster.markets, account)

  stash(positions)
  if (positions.length === 0) return []

  return positions.flatMap((p) => [
    // The hold-period stamp. `cooldownTimestamp + MIN_HOLD_PERIOD` is when
    // repay / redeem / transfer become possible again — a first-class gate on
    // every exit flow, not a display detail.
    { address: p.marketManager, name: 'accountAssets', params: [account] },
    // `(collateral, maxDebt, debt)` in WAD USD, priced by Curvance's own
    // pessimistic dual oracle — i.e. the numbers the protocol itself
    // liquidates against.
    { address: p.marketManager, name: 'statusOf', params: [account] },
    ...p.activeTokens.flatMap(({ cToken }) => [
      { address: cToken, name: 'balanceOf', params: [account] },
      { address: cToken, name: 'collateralPosted', params: [account] },
      { address: cToken, name: 'debtBalanceUpdated', params: [account] },
      // `assets per 1e18 shares` — the divisor is WAD regardless of the
      // cToken's own decimals. Read live: it drifts up with accrued interest,
      // so a cached rate under-reports a collateral balance.
      { address: cToken, name: 'exchangeRate', params: [] },
    ]),
  ])
}

/** Test seam — drops the roster + discovery caches. */
export function __resetCurvanceUserCaches(): void {
  rosterCache.clear()
  discoveryCache.clear()
}
